import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BatchAssessmentPanel = ({ onBatchAssess }) => {
  const [addresses, setAddresses] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState([]);

  const handleBatchAssess = async () => {
    const addressList = addresses?.split('\n')?.filter(addr => addr?.trim());
    if (addressList?.length === 0) return;

    setIsProcessing(true);
    
    // Mock batch assessment results
    const mockResults = addressList?.map((address, index) => ({
      address: address?.trim(),
      riskScore: Math.floor(Math.random() * 100),
      status: Math.random() > 0.8 ? 'High Risk' : Math.random() > 0.5 ? 'Medium Risk' : 'Low Risk',
      transactionCount: Math.floor(Math.random() * 1000) + 10,
      balance: (Math.random() * 100)?.toFixed(8),
      lastActivity: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString()
    }));

    setTimeout(() => {
      setResults(mockResults);
      setIsProcessing(false);
      onBatchAssess?.(mockResults);
    }, 2000);
  };

  const getRiskColor = (score) => {
    if (score >= 80) return 'text-error bg-red-50 border-red-200';
    if (score >= 60) return 'text-warning bg-amber-50 border-amber-200';
    if (score >= 40) return 'text-accent bg-yellow-50 border-yellow-200';
    return 'text-success bg-green-50 border-green-200';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-forensic-card">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Layers" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-text-primary">Batch Assessment</h3>
      </div>
      <div className="space-y-4">
        <Input
          label="Addresses to Assess"
          type="textarea"
          placeholder="Enter multiple addresses (one per line)&#10;1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa&#10;3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy&#10;bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh"
          value={addresses}
          onChange={(e) => setAddresses(e?.target?.value)}
          description="Enter one address per line for batch risk assessment"
          className="min-h-32"
        />

        <div className="flex items-center space-x-3">
          <Button
            onClick={handleBatchAssess}
            disabled={!addresses?.trim() || isProcessing}
            loading={isProcessing}
            iconName="Play"
            iconPosition="left"
          >
            {isProcessing ? 'Assessing...' : 'Start Batch Assessment'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setAddresses('');
              setResults([]);
            }}
            disabled={isProcessing}
          >
            Clear
          </Button>
        </div>
      </div>
      {results?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-text-primary">Assessment Results</h4>
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} color="currentColor" className="mr-2" />
              Export Results
            </Button>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {results?.map((result, index) => (
              <div key={index} className="border border-border rounded-lg p-4 hover:bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-data text-sm text-text-secondary truncate max-w-64">
                    {result?.address}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(result?.riskScore)}`}>
                    {result?.riskScore}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-text-secondary">Balance:</span>
                    <div className="font-medium text-text-primary">{result?.balance} BTC</div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Transactions:</span>
                    <div className="font-medium text-text-primary">{result?.transactionCount}</div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Last Activity:</span>
                    <div className="font-medium text-text-primary">{result?.lastActivity}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-error">{results?.filter(r => r?.riskScore >= 80)?.length}</div>
                <div className="text-xs text-text-secondary">High Risk</div>
              </div>
              <div>
                <div className="text-lg font-bold text-warning">{results?.filter(r => r?.riskScore >= 60 && r?.riskScore < 80)?.length}</div>
                <div className="text-xs text-text-secondary">Medium-High</div>
              </div>
              <div>
                <div className="text-lg font-bold text-accent">{results?.filter(r => r?.riskScore >= 40 && r?.riskScore < 60)?.length}</div>
                <div className="text-xs text-text-secondary">Medium</div>
              </div>
              <div>
                <div className="text-lg font-bold text-success">{results?.filter(r => r?.riskScore < 40)?.length}</div>
                <div className="text-xs text-text-secondary">Low Risk</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchAssessmentPanel;